import { I18n } from '@/i18n/I18n';
import { ConnectionConfigState } from '@/frontend/hooks/useConnectionConfig';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * The connection's configuration fields.
 *
 * Ported from veriform-desktop's `ConnectionConfig` (Equipment/
 * ConnectionConfig.tsx:67): a row per field, its value, and whether it is
 * optional. A field the provider restricts to a set of values is a dropdown;
 * anything else is a text box.
 */

export function ConnectionConfigFields(props: { config: ConnectionConfigState }) {
  const { config } = props;

  if (config.loading) {
    return <LoadingSpinner />;
  }

  if (config.error) {
    return <p className="text-danger mb-0">{I18n.get('connectionConfigurationUnavailable', { details: config.error })}</p>;
  }

  if (config.empty) {
    return <p className="text-muted mb-0">{I18n.get('noConnectionConfiguration')}</p>;
  }

  return (
    <table className="table table-hover">
      <thead>
        <tr>
          <th>{I18n.get('field')}</th>
          <th>{I18n.get('value')}</th>
          <th aria-label={I18n.get('optional')} />
        </tr>
      </thead>
      <tbody>
        { Object.keys(config.fields).map(key => (
          <tr key={key}>
            <td>
              <div style={{ height: '32px' }} className="d-flex align-items-center">{key}</div>
            </td>
            <td>
              <FieldValue fieldKey={key} config={config} />
            </td>
            <td>
              <div style={{ height: '32px' }} className="d-flex align-items-center">
                { config.isRequired(key) ? '' : `(${I18n.get('optional')})` }
              </div>
            </td>
          </tr>
        )) }
      </tbody>
    </table>
  );
}

function FieldValue(props: { fieldKey: string; config: ConnectionConfigState }) {
  const { fieldKey, config } = props;
  const allowed = config.schema.fullConnectionSchema[fieldKey]?.enum;

  if (allowed) {
    return (
      <select
        className="form-select form-select-sm"
        aria-label={fieldKey}
        value={config.fields[fieldKey] ?? ''}
        onChange={event => config.setField(fieldKey, event.target.value)}
      >
        <option value="">{I18n.get('select')}</option>
        { allowed.map(value => <option key={value} value={value}>{value}</option>) }
      </select>
    );
  }

  return (
    <input
      type="text"
      className="form-control form-control-sm"
      aria-label={fieldKey}
      value={config.fields[fieldKey] ?? ''}
      onChange={event => config.setField(fieldKey, event.target.value)}
    />
  );
}
